import { useBoardMembershipEffectiveMember } from './boardMembership';
import { useJrStaffBoardMembership } from './jrStaffBoard';
import { isDancerLoginUserDoc, isStaffRole, type UserDoc } from './users';

/**
 * WHETHER MESSAGES DRAWS STAFF ANNOUNCEMENT ROWS FOR HER, AND WHICH ONES.
 *
 * Moved here whole from Messages.tsx (B17 refute round one, finding 2) so the
 * Waiting on you count opens the same door Messages does. The count used to
 * turn announcement rows on for staff and the Interns group only, so a parent
 * standing in on the Jr Staff roll had each Jr Staff announcement card counted
 * as a row of its own while Messages folded it into the announcement row. One
 * hook now, called by Messages and by lib/messageWaiting.ts, so the two cannot
 * drift apart again. Every comment below is the one Messages carried.
 */

/** The fields of her own users doc the door reads. */
export type MessagesBoardDoorDoc = Pick<UserDoc, 'role' | 'status'>
  & Partial<Pick<UserDoc, 'studentId' | 'groups'>>;

/**
 * Messages draws staff announcement rows for an active account that is not a
 * dancer's own sign-in and is staff, in the Interns group, or on the Jr Staff
 * roll (the effective member answer, overlay included).
 */
export function messagesShowsBoardInbox(
  userDoc: MessagesBoardDoorDoc | null | undefined,
  onJrStaffRoll: boolean
): boolean {
  return userDoc?.status === 'active'
    && !isDancerLoginUserDoc(userDoc)
    && (isStaffRole(userDoc.role) || (userDoc.groups ?? []).includes('interns') || onJrStaffRoll);
}

export interface MessagesBoardDoor {
  showBoardInbox: boolean;
  onJrStaffRoll: boolean;
  jrStaffBoardRemoved: boolean;
}

export function useMessagesBoardDoor(
  user: { uid: string } | null | undefined,
  userDoc: MessagesBoardDoorDoc | null | undefined
): MessagesBoardDoor {
  // A parent standing in for a Jr Staff dancer now belongs on this list too,
  // for announcements addressed to Jr Staff and nothing else (Bobbie,
  // 2026-08-30). A live subscription on the derived roll doc, the same door
  // question the family home already asks for the Jr Staff board - and it
  // is only a question: shared/firestore.rules decides.
  //
  // ASKED FOR EVERY ACTIVE ACCOUNT, staff included, because a stand-in
  // guardian may ALSO work at the studio. A first pass gated this on
  // !isStaffRole and so never asked the question for a parent on the roll
  // whose own account is 'foh', 'company' or 'manager': a Jr-Staff-only
  // announcement does not name her role, so the audience check refused her
  // and this door was never even opened - while the server notify lane, which
  // never checked role against the roll, still emailed her the post the app
  // would not show. Roll membership is the authority on both sides now;
  // isJrStaffRollCaller in shared/firestore.rules is the boundary that
  // actually decides. The cost is one listener for staff who are not on
  // the roll, which is the same read the board's own door already makes.
  //
  // LIVE, NOT ONE-SHOT (PR #807 round 11, Codex P2): useJrStaffBoardMembership
  // used to getDoc this roll once on mount, so a guardian added to the roll
  // after Messages was already open never flipped jrStaffRollRaw to 'yes' -
  // the discussion query below stayed closed for her even though the
  // server's own notification for the post pointed straight back here, a
  // dead end until she reloaded the page. See the hook's own comment in
  // lib/jrStaffBoard.ts for the fix.
  //
  // THIS IS `autoIn`, not the final answer (task #34 rework, the same
  // "raw roll read" trap CompBoardEffectiveMember's own comment above
  // documents): jrStaffRollRaw alone ignores a Manage-members overlay entry,
  // so a director-added guardian saw no announcements until the hourly roll
  // sweep caught up, and a director-removed one kept seeing them. Folded
  // onto the SAME overlay useBoardMembershipEffectiveMember already reads
  // for the board itself - one effective-membership source, board and
  // announcement alike, exactly the ruling this lane exists to satisfy.
  const jrStaffRollRaw = useJrStaffBoardMembership(
    user?.uid ?? null,
    userDoc?.status === 'active' && !isDancerLoginUserDoc(userDoc)
  );
  const jrStaffRollAutoIn = Boolean(
    user
    && userDoc?.status === 'active'
    && !isDancerLoginUserDoc(userDoc)
    && jrStaffRollRaw === 'yes'
  );
  // `removed` (PR #807 round 8, Codex P1-a): this viewer's OWN Manage
  // members removal from the Jr Staff board, straight off the overlay -
  // stays true even after onJrStaffRoll flips back on a later roll sweep,
  // because every Jr Staff account also carries a matching staff role that
  // would otherwise re-admit them into their own Messages list. See
  // discussionReachesUser's own comment in lib/discussionAudience.ts.
  const {
    member: onJrStaffRoll,
    settled: jrStaffBoardOverlaySettled,
    removed: jrStaffBoardRemovedRaw,
    fromCache: jrStaffBoardOverlayFromCache,
    error: jrStaffBoardOverlayErrored,
  } = useBoardMembershipEffectiveMember(
    'jrStaffBoard',
    user?.uid,
    jrStaffRollAutoIn
  );
  // FAIL CLOSED WHILE THE OVERLAY IS UNSETTLED (Codex P1, PR #807 round 13).
  // `settled` stays false until the overlay's OWN first snapshot (success or
  // error) lands, but the role-query listeners usePersonalDiscussions opens
  // (in Messages and in the count) can and do serve a CACHED result before that - Firestore's local
  // cache has no notion of "wait for the overlay too". A removed member's
  // cached role-query snapshot would render her Jr-Staff-scoped row for
  // that unsettled window (jrStaffBoardRemovedRaw defaults to false against
  // PURE_EMPTY_OVERLAY - "not yet proven removed" reading as "not removed"),
  // then hide it the instant the overlay actually lands: a flash of fenced
  // content, not a permission leak, but the exact half-second the removal
  // fence exists to prevent. Treat unsettled as removed for the duration -
  // discussionReachesUser's own isJrStaffAudience gate makes this a no-op
  // for every row that is not Jr-Staff-scoped, so an ordinary announcement
  // never flickers - and settle to the true answer the moment the overlay's
  // first snapshot lands. The safer direction: a legitimate member briefly
  // not seeing her own row is a worse-than-necessary wait, never a leak; a
  // removed member briefly seeing a fenced row is the leak this exists to
  // close.
  //
  // SETTLED IS NOT THE SAME FACT AS SERVER-CONFIRMED (Codex P1, PR #807
  // round 14, the direct follow-on to the fix just above). With local
  // persistence, the overlay's FIRST snapshot on a fresh mount can itself be
  // served straight from cache - `settled` flips true, `jrStaffBoardOverlayFromCache`
  // stays true, and that cached snapshot may predate a removal that has
  // already happened server-side. Exactly the same trap: `removedRaw` reads
  // off data the server has not actually confirmed, which is
  // indistinguishable from "confirmed not removed" unless this caller also
  // asks. Folding fromCache into the same fence closes that: unconfirmed
  // (still cached) reads as removed, same fail direction as unsettled,
  // until the overlay's server-confirmed snapshot actually lands.
  //
  // NOR IS AN ERROR THE SAME AS A CONFIRMED CLEAR (Codex P2a, PR #807 round
  // 15, the direct follow-on to both fixes above). The overlay listener's
  // own error path used to reset to an empty overlay and report settled
  // true - which reads exactly like "server-confirmed, nobody removed" to
  // this fence, fail-OPEN the instant the listener errors. Folding the
  // hook's own `error` in closes it the same way: an error with no prior
  // confirmed data reads as removed. An error AFTER a confirmed answer
  // already landed does not need this term at all - the hook itself
  // preserves that last confirmed overlay across the error (see its own
  // comment in boardMembership.ts), so `jrStaffBoardRemovedRaw` (and
  // `jrStaffBoardOverlayFromCache`, if that confirmed answer was itself
  // still cached) already carry the right answer through unaffected.
  const jrStaffBoardRemoved =
    !jrStaffBoardOverlaySettled
    || jrStaffBoardOverlayFromCache
    || jrStaffBoardOverlayErrored
    || jrStaffBoardRemovedRaw;
  const showBoardInbox = messagesShowsBoardInbox(userDoc, onJrStaffRoll);
  return { showBoardInbox, onJrStaffRoll, jrStaffBoardRemoved };
}
